/** @format */

import { type ReactElement, useState, useEffect, useCallback } from "react";
import {
  Loader,
  Database,
  Video,
  Cpu,
  Film,
  Activity,
  Clock,
  TestTube,
  ClipboardList,
  Gauge,
  ShieldCheck,
  Thermometer,
  Shield,
} from "lucide-react";
import "../generate-sop/GenerateSOP.css";
import FreeTrialNotif from "../generate-sop/FreeTrialNotif";
import useFreeTrials from "../../hooks/useFreeTrials";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import "../../components/ui/Header.css";
import useSopAnalyze from "../../hooks/useSopAnalyze";
import { track } from "../../lib/posthog";
import { useToast } from "../../hooks/useToast";
import Toast from "../../components/ui/Toast";
import "../../components/ui/Toast.css";
import FlowUploader from "../../components/ui/FlowUploader";
import FlowConfig, { FlowConfigOption } from "../../components/ui/FlowConfig";
import SopTypeSelector, {
  SopTypeOption,
} from "../../components/ui/SopTypeSelector";
import { useOrganizationStore } from "../../store/useOrganizationStore";

export type FlowType = "video";

interface FlowDefinition {
  id: FlowType;
  title: string;
  description: string;
  icon: React.ReactNode;
  fileTypes: string[];
  acceptedMimeTypes: string[];
  maxFiles: number;
  configOptions: FlowConfigOption[];
}

const FLOWS: Record<FlowType, FlowDefinition> = {
  video: {
    id: "video",
    title: "Video Workflow",
    description: "Video clips to extract steps and workflows",
    icon: <Video className="w-8 h-8" />,
    fileTypes: [".mp4", ".mov", ".avi", ".mkv"],
    acceptedMimeTypes: [
      "video/mp4",
      "video/quicktime",
      "video/avi",
      "video/mkv",
    ],
    maxFiles: 10,
    configOptions: [
      {
        id: "frame_extraction",
        label: "Key frame extraction",
        description: "Pick important frames to document each step",
        default: true,
        icon: Film,
      },
      {
        id: "motion_analysis",
        label: "Motion analysis",
        description: "Hand movements and tool usage",
        default: true,
        icon: Activity,
      },
      {
        id: "timeline_generation",
        label: "Generate timeline",
        description: "Chronological step sequence",
        default: false,
        icon: Clock,
      },
    ],
  },
};

const CURRENT_FLOW = FLOWS.video;

export default function TestProcedures(): ReactElement {
  const [files, setFiles] = useState<File[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  void selectedOptions; // reserved for future API params; setter used by FlowConfig
  const [selectedTestType, setSelectedTestType] = useState<string | null>(
    "functional-test",
  );
  const [openFreeTrialNotif, setOpenFreeTrialNotif] = useState(false);
  const [fileNotes, setFileNotes] = useState<Record<string, string>>({});
  const [assemblyFile, setAssemblyFile] = useState<File | null>(null);
  const organizations = useOrganizationStore((s) => s.organizations);
  const activeOrgId = useOrganizationStore((s) => s.activeOrgId);
  const maxUploadBytes =
    organizations.find((o) => o.orgId === activeOrgId)?.maxUploadBytes ?? null;
  const { toast, showToast, hideToast } = useToast();
  const onSubmitSuccess = useCallback(() => {
    showToast(
      "Success",
      "Your files have been submitted for processing.",
      "success",
    );
  }, [showToast]);

  const {
    analyzeFile,
    analyzing,
    progressMessage,
    progressPercentage,
    error,
    formattedElapsedTime,
  } = useSopAnalyze(
    files,
    "",
    fileNotes,
    assemblyFile,
    onSubmitSuccess,
  );
  const {
    freeTrialsRemaining,
    freeTrialLimit,
    isFreeTrial,
    getFreeTrialsUsed,
  } = useFreeTrials();

  const testTypeOptions: SopTypeOption[] = [
    {
      id: "functional-test",
      label: "Functional Test",
      description: "Verify functions and behavior vs requirements",
      icon: TestTube,
    },
    {
      id: "incoming-quality-control",
      label: "Incoming Inspection",
      description: "Acceptance criteria for incoming materials and parts",
      icon: ClipboardList,
    },
    {
      id: "performance-test",
      label: "Performance Test",
      description: "Stress, load, and performance validation",
      icon: Gauge,
    },
    {
      id: "validation-verification",
      label: "Validation & Verification",
      description: "IQ/OQ/PQ and compliance validation protocols",
      disabled: true,
      icon: ShieldCheck,
    },
    {
      id: "environmental-test",
      label: "Environmental Test",
      description: "Temperature, humidity, and stress testing",
      disabled: true,
      icon: Thermometer,
    },
    {
      id: "safety-test",
      label: "Safety & Compliance Test",
      description: "Verify safety and regulatory requirements",
      disabled: true,
      icon: Shield,
    },
  ];

  useEffect(() => {
    if (isFreeTrial) {
      setOpenFreeTrialNotif(true);
    }
  }, [isFreeTrial]);

  const handleMoveLeft = useCallback((index: number) => {
    if (index > 0) {
      setFiles((prev) => {
        const newFiles = [...prev];
        [newFiles[index - 1], newFiles[index]] = [
          newFiles[index],
          newFiles[index - 1],
        ];
        return newFiles;
      });
    }
  }, []);

  const handleMoveRight = useCallback((index: number) => {
    setFiles((prev) => {
      if (index < prev.length - 1) {
        const newFiles = [...prev];
        [newFiles[index], newFiles[index + 1]] = [
          newFiles[index + 1],
          newFiles[index],
        ];
        return newFiles;
      }
      return prev;
    });
  }, []);

  const handleFilesChange = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
    if (newFiles.length > 0) {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, []);

  const handleFileNoteChange = useCallback((fileName: string, note: string) => {
    setFileNotes((prev) => ({
      ...prev,
      [fileName]: note,
    }));
  }, []);

  const handleAssemblyChange = useCallback((file: File | null) => {
    setAssemblyFile(file);
  }, []);

  const handleAnalyze = async () => {
    if (files.length > 0 && selectedTestType) {
      track("sop_analysis_started", {
        flow_type: "video",
        sop_type: selectedTestType,
        file_count: files.length,
        source: "test_procedures",
      });
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 100);

      await getFreeTrialsUsed();
      analyzeFile();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <div className="min-h-screen pt-20 overflow-x-hidden">
        <main className="pt-8 w-full mx-auto px-6 lg:px-10">
          <div className="max-w-[1440px] mx-auto pb-8">
            <HeroSection
              title="Generate Test Procedures"
              description="Testing protocols and validation from your media"
            />

            <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl pb-8 shadow-2xl shadow-black/50">
              <FreeTrialNotif
                open={openFreeTrialNotif}
                onOpen={() => { }}
                freeTrialsRemaining={freeTrialsRemaining}
                freeTrialLimit={freeTrialLimit}
              />

              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                    {CURRENT_FLOW.icon}
                  </div>
                  <h2 className="text-2xl font-semibold text-white">
                    {CURRENT_FLOW.title}
                  </h2>
                </div>

                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left panel: config + type */}
                  <div className="w-full lg:w-[380px] shrink-0 space-y-5">
                    <div>
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <span className="w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-[10px] font-bold text-blue-400">1</span>
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Configuration</span>
                      </div>
                      <FlowConfig
                        flowType={CURRENT_FLOW.title}
                        options={CURRENT_FLOW.configOptions}
                        onOptionsChange={setSelectedOptions}
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <span className="w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-[10px] font-bold text-blue-400">2</span>
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Test Type</span>
                      </div>
                      <SopTypeSelector
                        title="Test procedure type"
                        options={testTypeOptions}
                        selectedType={selectedTestType}
                        onTypeChange={setSelectedTestType}
                      />
                    </div>
                  </div>

                  {/* Right panel: file upload + actions */}
                  <div className="flex-1 min-w-0 pt-7">
                    <FlowUploader
                      flowType={CURRENT_FLOW.id}
                      fileTypes={CURRENT_FLOW.fileTypes}
                      acceptedMimeTypes={CURRENT_FLOW.acceptedMimeTypes}
                      maxFiles={CURRENT_FLOW.maxFiles}
                      onFilesChange={handleFilesChange}
                      files={files}
                      onMoveLeft={handleMoveLeft}
                      onMoveRight={handleMoveRight}
                      fileNotes={fileNotes}
                      onFileNoteChange={handleFileNoteChange}
                      assemblyFile={assemblyFile}
                      onAssemblyChange={handleAssemblyChange}
                      maxUploadBytes={maxUploadBytes}
                      actionSlot={
                        analyzing ? (
                          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 backdrop-blur-sm px-5 py-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Loader className="animate-spin w-4 h-4 text-blue-400" />
                                <span className="text-sm font-medium text-white">{progressMessage}</span>
                              </div>
                              <span className="text-xs text-slate-400">
                                {Math.round(progressPercentage)}% • {formattedElapsedTime}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-700/30 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${progressPercentage}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-700/30 bg-slate-900/30 px-5 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              {files.length > 0 && (
                                <span className="inline-flex items-center gap-1.5 text-xs bg-slate-800/60 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/30">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                  {files.length} file{files.length !== 1 ? "s" : ""}
                                </span>
                              )}
                              {selectedTestType && (
                                <span className="inline-flex items-center gap-1.5 text-xs bg-slate-800/60 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/30">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                  {testTypeOptions.find((o) => o.id === selectedTestType)?.label}
                                </span>
                              )}
                              {assemblyFile && (
                                <span className="inline-flex items-center gap-1.5 text-xs bg-slate-800/60 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/30">
                                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                                  Assembly
                                </span>
                              )}
                              <button
                                className="inline-flex items-center gap-1.5 text-xs text-slate-500 cursor-not-allowed ml-1"
                                disabled
                              >
                                <Database className="w-3.5 h-3.5" />
                                PLM
                              </button>
                            </div>
                            <button
                              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                                !files.length || !selectedTestType || analyzing
                                  ? "opacity-50 cursor-not-allowed bg-slate-600/10 border border-slate-600/20 text-slate-400"
                                  : "cursor-pointer bg-gradient-to-r from-blue-500/20 to-blue-500/10 border border-blue-500/30 text-blue-400 hover:from-blue-500/30 hover:to-blue-500/20 hover:border-blue-500/40 hover:text-blue-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
                              }`}
                              onClick={handleAnalyze}
                              disabled={!files.length || !selectedTestType || analyzing}
                            >
                              <Cpu className="w-4 h-4" />
                              <span>Analyze {CURRENT_FLOW.title}</span>
                            </button>
                          </div>
                        )
                      }
                    />

                    {error && !analyzing && (
                      <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="font-medium">Error</span>
                        </div>
                        <p className="mt-2 text-sm">{error}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
    </div>
  );
}
